import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

interface IntroPageProps {
  onGuestMode: () => void;
}

const IntroPage: React.FC<IntroPageProps> = ({ onGuestMode }) => {
  const { login } = useAuth();
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [loginStep, setLoginStep] = useState('idle'); // 'idle', 'authenticating', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleGoogleLogin = async () => {
    try {
      setIsLoginLoading(true);
      setLoginStep('authenticating');

      // 개발 모드에서는 자동으로 성공 처리
      if (process.env.NODE_ENV === 'development') {
        console.log('🔧 개발 모드 - 로그인 자동 성공 처리');
        setLoginStep('success');

        // AuthContext의 login 함수를 호출하여 개발용 사용자 설정
        try {
          await login();
        } catch (devError) {
          console.log('🔧 개발 모드 - 백엔드 없이 로컬 처리됨');
          // 백엔드가 없어도 AuthContext에서 자동으로 개발용 사용자 설정됨
        }

        setTimeout(() => {
          setIsLoginLoading(false);
        }, 500);
        return;
      }

      await login();

      setLoginStep('success');
      // 로그인 성공 후 약간의 지연을 두고 대시보드로 이동
      setTimeout(() => {
        // AuthContext의 user 상태가 업데이트되면 App.tsx에서 자동으로 대시보드를 렌더링함
      }, 500);

    } catch (error) {
      console.error('로그인 실패:', error);
      setLoginStep('error');
      
      const errorStr = error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.';
      setErrorMessage(errorStr);
      
      // 5초 후 다시 시도할 수 있도록 상태 초기화
      setTimeout(() => {
        setLoginStep('idle');
        setErrorMessage('');
      }, 5000);
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handleGoogleLoginRedirect = () => {
    // Google 로그인 페이지로 직접 이동
    window.open('https://accounts.google.com/signin', '_blank', 'width=500,height=600');
  };

  // 에러 메시지에 따라 도움말 버튼을 보여줄지 결정
  const shouldShowGoogleLoginHelp = () => {
    return errorMessage.includes('브라우저에 Google 계정이 로그인되어 있지 않습니다') ||
           errorMessage.includes('시간이 초과되었습니다') ||
           errorMessage.includes('차단했습니다') ||
           errorMessage.includes('로그인이 취소되었습니다');
  };

  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: '#f5f6fa',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    padding: '60px 50px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)',
    border: '1px solid #e1e5e9',
    textAlign: 'center',
    maxWidth: '600px',
    width: '100%'
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '2.2rem',
    fontWeight: '600',
    color: '#172b4d',
    marginBottom: '12px'
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: '1.1rem',
    color: '#5e6c84',
    marginBottom: '40px',
    lineHeight: '1.6',
    maxWidth: '500px',
    margin: '0 auto 40px auto'
  };

  const buttonContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
    marginBottom: '32px'
  };

  const loginButtonStyle: React.CSSProperties = {
    backgroundColor: '#0052cc',
    color: 'white',
    border: 'none',
    borderRadius: '3px',
    padding: '16px 32px',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    minHeight: '48px'
  };

  const guestButtonStyle: React.CSSProperties = {
    backgroundColor: 'transparent',
    color: '#5e6c84',
    border: '1px solid #dfe1e6',
    borderRadius: '3px',
    padding: '16px 32px',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minHeight: '48px'
  };

  // 로딩 스피너 컴포넌트
  const LoadingSpinner = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      style={{
        animation: 'spin 1s linear infinite'
      }}
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
        fill="none"
        strokeDasharray="31.416"
        strokeDashoffset="31.416"
        style={{
          animation: 'dash 1.5s ease-in-out infinite'
        }}
      />
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes dash {
            0% {
              stroke-dasharray: 1, 150;
              stroke-dashoffset: 0;
            }
            50% {
              stroke-dasharray: 90, 150;
              stroke-dashoffset: -35;
            }
            100% {
              stroke-dasharray: 90, 150;
              stroke-dashoffset: -124;
            }
          }
        `}
      </style>
    </svg>
  );

  // 체크마크 아이콘
  const CheckIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path
        d="M20 6L9 17L4 12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  // 버튼 텍스트와 아이콘 결정
  const getButtonContent = () => {
    switch (loginStep) {
      case 'authenticating':
        return (
          <>
            <LoadingSpinner />
            로그인 중...
          </>
        );
      case 'success':
        return (
          <>
            <CheckIcon />
            로그인 완료
          </>
        );
      case 'error':
        return (
          <>
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            다시 시도
          </>
        );
      default:
        return (
          <>
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
{process.env.NODE_ENV === 'development' ? '개발 모드로 시작' : 'Google로 로그인하고 시작'}
          </>
        );
    }
  };

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        {process.env.NODE_ENV === 'development' && (
          <div style={{
            backgroundColor: '#ff9500',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '4px',
            fontSize: '0.9rem',
            fontWeight: '600',
            marginBottom: '20px',
            boxShadow: '0 2px 4px rgba(255, 149, 0, 0.3)'
          }}>
            🔧 개발 모드 - 스텁 인증 활성화
          </div>
        )}

        <h1 style={titleStyle}>사이트 성능 분석</h1>
        <p style={subtitleStyle}>
          웹사이트의 성능, 기술 스택, SEO 상태를 종합적으로 분석하여<br/>
          실행 가능한 개선 방안을 제시합니다
          {process.env.NODE_ENV === 'development' && (
            <><br/><span style={{ color: '#ff6b35', fontWeight: '600' }}>
              개발 모드에서는 실제 API 호출 없이 모의 데이터로 동작합니다
            </span></>
          )}
        </p>

        <div style={buttonContainerStyle}>
          <button
            style={{
              ...loginButtonStyle,
              backgroundColor: loginStep === 'success' ? '#00875a' : 
                              loginStep === 'error' ? '#de350b' : 
                              loginStep === 'authenticating' ? '#0747a6' : '#0052cc',
              cursor: isLoginLoading ? 'wait' : 'pointer',
              opacity: isLoginLoading ? 0.8 : 1
            }}
            onClick={handleGoogleLogin}
            disabled={isLoginLoading || loginStep === 'success'}
            onMouseOver={(e) => {
              if (!isLoginLoading && loginStep !== 'success') {
                const currentBg = loginStep === 'error' ? '#bf2600' : '#0747a6';
                e.currentTarget.style.backgroundColor = currentBg;
              }
            }}
            onMouseOut={(e) => {
              if (!isLoginLoading) {
                const originalBg = loginStep === 'success' ? '#00875a' : 
                                 loginStep === 'error' ? '#de350b' : 
                                 loginStep === 'authenticating' ? '#0747a6' : '#0052cc';
                e.currentTarget.style.backgroundColor = originalBg;
              }
            }}
          >
            {getButtonContent()}
          </button>

          <button
            style={{
              ...guestButtonStyle,
              opacity: isLoginLoading ? 0.5 : 1,
              cursor: isLoginLoading ? 'not-allowed' : 'pointer'
            }}
            onClick={onGuestMode}
            disabled={isLoginLoading}
            onMouseOver={(e) => {
              if (!isLoginLoading) {
                e.currentTarget.style.borderColor = '#b3bac5';
                e.currentTarget.style.color = '#42526e';
                e.currentTarget.style.backgroundColor = '#f4f5f7';
              }
            }}
            onMouseOut={(e) => {
              if (!isLoginLoading) {
                e.currentTarget.style.borderColor = '#dfe1e6';
                e.currentTarget.style.color = '#5e6c84';
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            게스트로 시작
          </button>
        </div>

        {/* 에러 메시지와 도움말 */}
        {loginStep === 'error' && errorMessage && (
          <div style={{
            marginTop: '20px',
            padding: '16px',
            backgroundColor: '#ffebee',
            borderRadius: '6px',
            border: '1px solid #ffcdd2'
          }}>
            <div style={{
              fontSize: '14px',
              color: '#c62828',
              textAlign: 'center',
              marginBottom: shouldShowGoogleLoginHelp() ? '12px' : '0'
            }}>
              {errorMessage}
            </div>
            
            {shouldShowGoogleLoginHelp() && (
              <div style={{
                textAlign: 'center'
              }}>
                <button
                  onClick={handleGoogleLoginRedirect}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#1976d2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#1565c0';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#1976d2';
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Google에서 로그인하기
                </button>
              </div>
            )}
          </div>
        )}

        <div style={{ 
          marginTop: '32px', 
          padding: '20px', 
          backgroundColor: '#f4f5f7', 
          borderRadius: '3px',
          border: '1px solid #dfe1e6'
        }}>
          <div style={{ 
            fontSize: '0.95rem',
            color: '#5e6c84',
            textAlign: 'center',
            lineHeight: '1.4'
          }}>
            <strong style={{ color: '#42526e' }}>게스트 모드:</strong> 분석 기능은 모두 이용 가능하지만 결과 저장/불러오기 불가
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntroPage;